import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import type { TCandidateBookedInterview, TCandidateBooking, TCandidateMessage, TCandidateSlot } from './types'

const resource = 'recruitmentProcess'

// Timestamps: a slot is a moment, not a calendar day
const bookedFromApi = (booked: any): TCandidateBookedInterview | null => booked && {
  ...booked,
  start: booked.start ? new Date(booked.start) : null,
  end: booked.end ? new Date(booked.end) : null,
}

/** The interviews of the candidate to book, or booked, in each of their processes */
const getBookings = (axios: IFactoryFunctionRequest<TCandidateBooking[], undefined>) => (): IFactoryResponse<TCandidateBooking[], undefined> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/candidateSpace/bookings`,
    afterReceivingHook: (bookings: any[]) => bookings.map(booking => ({
      ...booking,
      interviews: booking.interviews.map((interview: any) => ({ ...interview, booked: bookedFromApi(interview.booked) })),
    })),
  })
}

/** The slots the candidate can book for one planned interview */
const getSlots = (axios: IFactoryFunctionRequest<TCandidateSlot[], undefined>) => ({ processId, planInterviewId }: { processId: string, planInterviewId: string }): IFactoryResponse<TCandidateSlot[], undefined> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/candidateSpace/${processId}/slots`,
    query: { planInterviewId },
    afterReceivingHook: (slots: any[]) => slots.map(slot => ({ ...slot, start: new Date(slot.start), end: new Date(slot.end) })),
  })
}

const book = (axios: IFactoryFunctionRequest<{ interviewId: string, booked: TCandidateBookedInterview }, any>) => ({ processId, planInterviewId, meetingId }: { processId: string, planInterviewId: string, meetingId: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/candidateSpace/${processId}/bookings`,
    data: { planInterviewId, meetingId },
    afterReceivingHook: (response: any) => ({ ...response, booked: bookedFromApi(response.booked) }),
  })
}

const messageFromApi = (message: any): TCandidateMessage => ({ ...message, _created: new Date(message._created) })

/** The candidate's messages with the recruiters about one process, oldest first */
const getMessages = (axios: IFactoryFunctionRequest<TCandidateMessage[], undefined>) => (processId: string): IFactoryResponse<TCandidateMessage[], undefined> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/candidateSpace/${processId}/messages`,
    afterReceivingHook: (messages: any[]) => messages.map(messageFromApi),
  })
}

const sendMessage = (axios: IFactoryFunctionRequest<TCandidateMessage, { text: string }>) => ({ processId, text }: { processId: string, text: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/candidateSpace/${processId}/messages`,
    data: { text },
    afterReceivingHook: messageFromApi,
  })
}

export default (axios: AxiosInstance) => ({
  getBookings: getBookings(useAxios(axios)),
  getSlots: getSlots(useAxios(axios)),
  book: book(useAxios(axios)),
  getMessages: getMessages(useAxios(axios)),
  sendMessage: sendMessage(useAxios(axios)),
})
